import React from 'react'
import { Layout, Header, Showcase, SectionTitle } from '../../../../components'
import { Link } from 'gatsby'
import data from './data.json'
import persistent from '../../../../images/persistent-act-group.png'
import embedded from '../../../../images/embedded-act-group.png'

export default props => {
  const code = null

  return (
    <>
      <Header
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
        exclusive={true}
      />
      <Layout
        complete={true}
        category="Overview"
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
      >
        <div class="intro">
          <p>
            Action groups are a common way to present actions that apply to an
            entire page or parent element. The pattern includes at least one{' '}
            <Link className="link" to="/mt/components/button/design/">
              button
            </Link>{' '}
            and up to one{' '}
            <Link className="link" to="/mt/components/link/design">
              link
            </Link>
            .
          </p>
        </div>
        <SectionTitle>Usage</SectionTitle>

        {/* <Showcase
          site={data.site}
          title="Single"
          text="Used when there is only one action that applies to the page or parent element."
          code={code}
        >
          <div class="p-action-group">
            <div class="p-action-group__button-container">
              <button class="c-button--primary p-action-group__button">
                Submit
              </button>
            </div>
          </div>
        </Showcase> */}

        <Showcase
          site={data.site}
          title="Dual Button"
          text={
            <p>
              Used when there are two actions that apply to the page or parent
              element and when the buttons are small enough to appear inline.
            </p>
          }
          code={code}
        >
          <div class="p-action-group">
            <div class="p-action-group__button-container">
              <button class="c-button p-action-group__button">Cancel</button>
              <button class="c-button--primary p-action-group__button">
                Continue
              </button>
            </div>
          </div>
        </Showcase>
        <Showcase
          site={data.site}
          title="Dual Button (Stacked)"
          text={
            <p>
              Used when there are two actions that apply to the page or parent
              element but button width precludes inline treatement. This variant
              is preferred at smaller breakpoints but should not be used in
              full-width layouts at larger breakpoints.
            </p>
          }
          note="Due to the height of the container, stacked action group treatments generally should not be persistent."
          code={code}
        >
          <div class="p-action-group">
            <div class="p-action-group__button-container">
              <button class="c-button--primary p-action-group__button">
                Apply Now
              </button>
            </div>
            <div class="p-action-group__link-container">
              <button class="c-button p-action-group__button">
                Learn More
              </button>
            </div>
          </div>
        </Showcase>
        <Showcase
          site={data.site}
          title="Button + Link"
          text="Used to present a navigational link along with a single action that applies to the page or parent element."
          code={code}
        >
          <div class="p-action-group">
            <div class="p-action-group__button-container">
              <button class="c-button--primary p-action-group__button">
                Apply Now
              </button>
            </div>
            <div class="p-action-group__link-container">
              <a href="/#" class="c-link">
                Learn more
              </a>
            </div>
          </div>
        </Showcase>

        <Showcase
          site={data.site}
          title="Dual Button + Link"
          text={
            <p>
              Used to present a navigational link along with two actions that
              apply to the page or parent element and when the buttons are small
              enough to appear inline.
            </p>
          }
          code={code}
        >
          <div class="p-action-group">
            <div class="p-action-group__button-container">
              <button class="c-button p-action-group__button">Edit User</button>
              <button class="c-button--primary p-action-group__button">
                Add User
              </button>
            </div>
            <div class="p-action-group__link-container">
              <a href="/#" class="c-link">
                Go back
              </a>
            </div>
          </div>
        </Showcase>
        <Showcase
          site={data.site}
          title="Dual Button + Link (Stacked)"
          text={
            <p>
              Used to present a navigational link along with two actions that
              apply to the page or parent element but button width precludes
              inline treatment. This variant is preferred at smaller breakpoints
              but should not be used in full-width layouts at larger
              breakpoints.
            </p>
          }
          note="Due to the height of the container, stacked action group treatments generally should not be persistent."
          code={code}
        >
          <div class="p-action-group">
            <div class="p-action-group__button-container">
              <button class="c-button--primary p-action-group__button">
                Apply Now
              </button>
            </div>
            <div class="p-action-group__link-container">
              <button class="c-button p-action-group__button">
                Learn More
              </button>
            </div>
            <div class="p-action-group__link-container">
              <a href="/#" class="c-link">
                Go back
              </a>
            </div>
          </div>
        </Showcase>
        <hr />
        <SectionTitle>Design Environment</SectionTitle>
        <p>
          When there is no overflow at the bottom of the screen, action groups
          will appear embedded below and at the same{' '}
          <Link
            className="link"
            to={'/' + data.site + '/guidelines/elevation/design'}
          >
            elevation
          </Link>{' '}
          as the parent content they act on. When there is overflow, the action
          group can be displayed in either the embedded or the persistent
          position.
        </p>

        <Showcase
          site={data.site}
          title="Embedded"
          text="Used to reveal the action group only once the user has scrolled through the parent content."
        >
          <img
            width="50%"
            src={embedded}
            alt="screenshot of embedded action group"
          />
        </Showcase>
        <Showcase
          site={data.site}
          title="Persistent"
          text={
            <p>
              Used to continuously show the action group on an elevated plane
              while the user is scrolling through the parent content. The
              container is fixed to the bottom of the parent element, fills the
              full width, and uses a medium{' '}
              <Link
                class="link"
                to={'/' + data.site + '/utilities/shadow/design'}
              >
                shadow
              </Link>
              . Consider the amount of screen space the stacked dual button and
              stacked dual button + link variants take up before using the
              persistent placement.
            </p>
          }
        >
          <img
            width="50%"
            src={persistent}
            alt="screenshot of persistent action group"
          />
        </Showcase>
        <hr />
        <SectionTitle>Microcopy</SectionTitle>
        <p>
          Follow the guidelines for the{' '}
          <Link class="link" to={'/' + data.site + '/components/button/design'}>
            button
          </Link>{' '}
          and{' '}
          <Link class="link" to={'/' + data.site + '/components/link/design'}>
            link
          </Link>{' '}
          components.
        </p>
      </Layout>
    </>
  )
}
